import React, { useState, ChangeEvent, useEffect } from 'react';
import styles from './style.module.css';
import { BotiqueLayoutImage, BoutiqueLayoutOverlay } from '../../assets/webp';
import { StoreAIBot } from '../../components/StoreAIBot';
import Typography from '../../components/Typography/Typography';
import { StoreCart, StoreCloudUpload, StoreImgName, StoreSearchIconDark } from '../../assets/svgs';
import FlatButtons from '../../components/FlatButton/FlatButton';
import { useDispatch, useSelector } from 'react-redux';
import { setName, setLogoFile, setDescription, setCategory, setTags, setLocation } from "../../redux/slices/createStoreSlice"
import { RootState } from '../../redux/store';
import { useNavigate, useParams } from 'react-router-dom';
import { StorePreviewModal } from '../../components/StorePreviewModal';
import AnimatedButton from '../../components/AnimatedButton';
import { useCreateStoreMutation, useGetTemplateByIdQuery } from '../../redux/services/storesApi';
import { setStoreTemplate, setStoreId } from '../../redux/slices/createStoreSlice';
// import { resetStoreState } from '../../redux/slices/createProductSlice';

interface BoutiqueInfoProps {
    backgroundImage?: string;
    overlayImage?: string;
}

interface FormData {
    storeName: string;
    storeDescription: string;
    category: string;
    addTag: string;
    location: string;
}

export const BoutiqueInfo: React.FC<BoutiqueInfoProps> = ({
    backgroundImage = BotiqueLayoutImage,
    overlayImage = BoutiqueLayoutOverlay
}) => {
    const [formData, setFormData] = useState<FormData>({
        storeName: '',
        storeDescription: '',
        category: '',
        addTag: '',
        location: ''
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { templateId } = useParams<{ templateId: string }>();
    const storeDetails = useSelector((state: RootState) => state.CreateStore);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [logoFile, setFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { data: templateData, isLoading, isError } = useGetTemplateByIdQuery(
        templateId || '',
        { skip: !templateId }
    );

    const [createStore, { isLoading: isCreatingStore }] = useCreateStoreMutation();

    const currentBackgroundImage = templateData?.image_preview_url || backgroundImage;
    const currentOverlayImage = templateData?.model_url || overlayImage;
    const templateName = templateData?.name || 'Store Name';
    const currentTemplateId = templateId || templateData?._id || '';

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    const handlePreviewStore = () => {
        if (isMobile) {
            setIsModalOpen(true);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const handleInputChange = (field: keyof FormData, value: string): void => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (errorMessage) {
            setErrorMessage('');
        }
    };

    const handleFileUpload = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setErrorMessage('Please upload a valid image file.');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                setErrorMessage('Logo file size must be less than 5MB.');
                return;
            }
            setFile(file);
            const previewUrl = URL.createObjectURL(file);
            setLogoPreview(previewUrl);
            console.log('Logo uploaded:', file.name);
        }
    };

    useEffect(() => {
        return () => {
            if (logoPreview) {
                URL.revokeObjectURL(logoPreview);
            }
        };
    }, [logoPreview]);

    useEffect(() => {
        if (errorMessage) {
            console.log('Validation error:', errorMessage);
        }
    }, [errorMessage]);

    useEffect(() => {
        if (storeDetails) {
            console.log("Store details object:", storeDetails);
            console.log("Store Template:", storeDetails.storeTemplate);
        } else {
            console.log("No store details found in Redux");
        }
    }, [storeDetails, dispatch])

    const handleStoreDetails = async () => {
        setErrorMessage('');
        setIsSubmitting(true);

        if (!formData.storeName.trim()) {
            setErrorMessage('Store name is required.');
            setIsSubmitting(false);
            return;
        }

        if (!formData.storeDescription.trim()) {
            setErrorMessage('Store description is required.');
            setIsSubmitting(false);
            return;
        } else if (formData.storeDescription.length < 10) {
            setErrorMessage('Description must be at least 10 characters.');
            setIsSubmitting(false);
            return;
        }

        if (!formData.category.trim()) {
            setErrorMessage('Category is required.');
            setIsSubmitting(false);
            return;
        }


        if (!currentTemplateId) {
            setErrorMessage('Template ID is missing. Please select a template first.');
            setIsSubmitting(false);
            return;
        }

        try {
            const apiFormData = new FormData();
            apiFormData.append('name', formData.storeName.trim());
            apiFormData.append('description', formData.storeDescription.trim());
            apiFormData.append('category', formData.category.trim());
            apiFormData.append('template_id', currentTemplateId);

            const tags = formData.addTag.trim().split(/[\s,]+/).filter(tag => tag.length > 0);
            if (tags.length > 0) {
                tags.forEach(tag => {
                    apiFormData.append('tags', tag);
                });
            } else {
                apiFormData.append('tags', '');
            }

            if (formData.location.trim()) {
                apiFormData.append('location', formData.location.trim());
            }


            if (logoFile) {
                apiFormData.append('logo_file', logoFile);

                const logoFileBase64 = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(logoFile);
                });
                dispatch(setLogoFile(logoFileBase64));
            }

            const response = await createStore(apiFormData).unwrap();

            console.log('Store created successfully:', response);

            dispatch(setName(formData.storeName.trim()));
            dispatch(setDescription(formData.storeDescription.trim()));
            dispatch(setCategory(formData.category.trim()));
            dispatch(setTags(tags));
            dispatch(setLocation(formData.location.trim()));
            dispatch(setStoreTemplate(response.template_id));
            dispatch(setStoreId(response._id));

            navigate(`/add-products/${response._id}`);
        } catch (error: any) {
            console.error('Failed to create store:', error);

            let errorMsg = 'Failed to create store. Please try again.';

            if (error?.data?.detail) {
                if (Array.isArray(error.data.detail)) {
                    errorMsg = error.data.detail.map((e: any) => e.msg).join(', ');
                } else if (typeof error.data.detail === 'string') {
                    errorMsg = error.data.detail;
                }
            } else if (error?.data?.message) {
                errorMsg = error.data.message;
            }

            setErrorMessage(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackNavigation = () => {
        if (currentTemplateId) {
            navigate(`/boutique-layout/${currentTemplateId}`);
        } else {
            navigate('/choose-template');
        }
    };

    return (
        <div className={styles.boutiqueContainer}>
            <div className={styles.contentWrapper}>
                <div className={styles.header}>
                    <Typography
                        variant='heading5'
                        fontFamily='satoshi'
                        weight='bold'
                        className={styles.title}>
                        Step 2: Add Some Information
                    </Typography>

                    {isMobile && (
                        <div className={styles.headerActions}>
                            <AnimatedButton
                                variant='secondary'
                                onClick={handlePreviewStore}
                                className={styles.previewStoreBtn}
                            >
                                <img
                                    src={StoreImgName}
                                    alt="Welcome Name Icon"
                                    className={styles.icon}
                                />
                                <Typography
                                    variant='buttonText'
                                    fontFamily='satoshi'
                                    weight='regular'
                                    size='base'
                                    className={styles.previewStoreBtnText}
                                >
                                    Preview Store
                                </Typography>
                            </AnimatedButton>
                        </div>
                    )}
                </div>

                <div className={styles.mainContent}>
                    <div className={styles.contentGrid}>
                        <div className={styles.leftPanel}>
                            <div className={styles.section}>
                                <Typography
                                    variant='heading3'
                                    fontFamily='satoshi'
                                    weight='bold'
                                    className={styles.sectionTitle}>
                                    Store Information
                                </Typography>

                                {errorMessage && (
                                    <div className={styles.generalError}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.generalErrorText}>
                                            {errorMessage}
                                        </Typography>
                                    </div>
                                )}

                                <div className={styles.formGroup}>
                                    <label htmlFor="name" className={styles.formLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Store Name *
                                        </Typography>
                                    </label>
                                    <input
                                        type="text"
                                        id='name'
                                        placeholder="Add your store name"
                                        value={formData.storeName}
                                        onChange={(e) => handleInputChange('storeName', e.target.value)}
                                        className={`${styles.formInput} ${errorMessage && !formData.storeName.trim() ? styles.formInputError : ''}`}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="logo-upload" className={styles.formLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Store Logo (Optional)
                                        </Typography>
                                    </label>
                                    <div className={styles.uploadArea}>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileUpload}
                                            className={styles.fileInput}
                                            id="logo-upload"
                                        />
                                        <label htmlFor="logo-upload" className={styles.uploadLabel}>
                                            {logoPreview ? (
                                                <div className={styles.imagePreview}>
                                                    <img
                                                        src={logoPreview}
                                                        alt="Logo preview"
                                                        className={styles.previewImage}
                                                    />
                                                </div>
                                            ) : (
                                                <>
                                                    <div className={styles.uploadIcon}>
                                                        <img
                                                            src={StoreCloudUpload}
                                                            alt='store upload icon'
                                                        />
                                                    </div>
                                                    <span className={styles.uploadText}>
                                                        <Typography
                                                            variant='bodyRegular'
                                                            fontFamily='satoshi'
                                                            weight='regular'
                                                            size='base'
                                                            className={styles.labelText}>
                                                            Drag and drop files here
                                                        </Typography>
                                                    </span>
                                                </>
                                            )}
                                        </label>
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor='description' className={styles.formLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Store Description *
                                        </Typography>
                                    </label>
                                    <textarea
                                        id='description'
                                        placeholder="Enter store description..."
                                        value={formData.storeDescription}
                                        onChange={(e) => handleInputChange('storeDescription', e.target.value)}
                                        className={`${styles.formTextarea} ${errorMessage && (!formData.storeDescription.trim() || formData.storeDescription.length < 10) ? styles.formInputError : ''}`}
                                        rows={6}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor='category' className={styles.formLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Category *
                                        </Typography>
                                    </label>
                                    <select
                                        id='category'
                                        title='Category tab'
                                        value={formData.category}
                                        onChange={(e) => handleInputChange('category', e.target.value)}
                                        className={`${styles.formSelect} ${errorMessage && !formData.category.trim() ? styles.formInputError : ''}`}
                                    >
                                        <option value="">Select category...</option>
                                        <option value="clothing">Clothing & Apparel</option>
                                        <option value="electronics">Electronics</option>
                                        <option value="home">Home & Garden</option>
                                        <option value="beauty">Beauty & Personal Care</option>
                                        <option value="sports">Sports & Outdoors</option>
                                        <option value="books">Books & Media</option>
                                        <option value="toys">Toys & Games</option>
                                        <option value="food">Food & Beverages</option>
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="tag" className={styles.formLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Add Tags (Optional)
                                        </Typography>
                                    </label>
                                    <input
                                        type="text"
                                        id='tag'
                                        placeholder="Add tags separated by commas"
                                        value={formData.addTag}
                                        onChange={(e) => handleInputChange('addTag', e.target.value)}
                                        className={styles.formInput}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="location" className={styles.formLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Location (Optional)
                                        </Typography>
                                    </label>
                                    <input
                                        type="text"
                                        id='location'
                                        placeholder="Add your location"
                                        value={formData.location}
                                        onChange={(e) => handleInputChange('location', e.target.value)}
                                        className={styles.formInput}
                                    />
                                </div>

                                {isMobile && (
                                    <div className={styles.mobileButtons}>
                                        <AnimatedButton
                                            variant='secondary'
                                            onClick={handlePreviewStore}
                                            className={styles.previewDemoBtn}
                                        >
                                            <Typography
                                                variant='buttonText'
                                                fontFamily='satoshi'
                                                weight='regular'
                                                size='base'
                                                className={styles.previewDemoBtnText}
                                            >
                                                Preview Demo Store
                                            </Typography>
                                        </AnimatedButton>
                                    </div>
                                )}
                            </div>
                        </div>

                        {!isMobile && (
                            <div className={styles.rightPanel}>
                                <div className={styles.storeContainer}>
                                    <div className={styles.topNav}>
                                        <FlatButtons className={styles.storeName}>
                                            <img
                                                src={StoreImgName}
                                                alt="Welcome Name Icon"
                                                className={styles.icon}
                                            />
                                            <Typography
                                                variant='buttonText'
                                                fontFamily='satoshi'
                                                weight='regular'
                                                size='base'
                                                className={styles.storeNameText}>
                                                {formData.storeName || templateName}
                                            </Typography>
                                        </FlatButtons>
                                        <FlatButtons className={styles.navIcons}>
                                            <img
                                                src={StoreSearchIconDark}
                                                alt="Search Icon"
                                                className={styles.icon}
                                            />
                                            <img
                                                src={StoreCart}
                                                alt="Cart Icon"
                                                className={styles.icon}
                                            />
                                        </FlatButtons>
                                    </div>

                                    <div className={styles.storeLayout}>
                                        <img
                                            src={currentBackgroundImage}
                                            alt="Store background"
                                            className={styles.backgroundImage}
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.src = backgroundImage;
                                            }}
                                        />

                                        {currentOverlayImage && (
                                            <div className={styles.overlay}>
                                                <img
                                                    src={currentOverlayImage}
                                                    alt="Store overlay"
                                                    className={styles.overlayImage}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src = overlayImage;
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={styles.actionSection}>
                        <div className={styles.navigationContainer}>
                            <AnimatedButton
                                variant='primary'
                                onClick={handleBackNavigation}
                                className={styles.previousBtn}
                                disabled={isSubmitting || isCreatingStore}
                            >
                                <Typography
                                    variant='buttonText'
                                    fontFamily='satoshi'
                                    weight='regular'
                                    size='base'
                                    className={styles.previousBtnText}>
                                    Previous
                                </Typography>
                            </AnimatedButton>

                            <AnimatedButton
                                variant='primary'
                                onClick={handleStoreDetails}
                                className={styles.nextBtn}
                                disabled={isSubmitting || isCreatingStore}
                            >
                                <Typography
                                    variant='buttonText'
                                    fontFamily='satoshi'
                                    weight='regular'
                                    size='base'
                                    className={styles.nextBtnText}>
                                    {isSubmitting || isCreatingStore ? 'Creating...' : 'Next'}
                                </Typography>
                            </AnimatedButton>
                        </div>
                        <div className={styles.progressSection}>
                            <div className={styles.progressContainer}>
                                <Typography
                                    variant='bodyLarge'
                                    fontFamily='satoshi'
                                    weight='bold'
                                    className={styles.stepText}>
                                    Step 2 of 4
                                </Typography>
                                <div className={styles.progressBar}>
                                    <div className={styles.progressFilled}>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <StorePreviewModal
                    isOpen={isModalOpen}
                    onClose={closeModal}
                    backgroundImage={currentBackgroundImage}
                    overlayImage={currentOverlayImage}
                    storeName={formData.storeName || templateName}
                />
            )}

            <StoreAIBot />
        </div>
    );
};